import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import es from './es';
import en from './en';

export const SUPPORTED_LANGUAGES = ['es', 'en'];
export const LANGUAGE_STORAGE_KEY = 'q_language';

const dictionaries = { es, en };

export function normalizeLanguage(value) {
  const raw = String(value || '').toLowerCase().trim();
  if (raw.startsWith('en')) return 'en';
  if (raw.startsWith('es')) return 'es';
  return 'es';
}

export function getCurrentLanguage() {
  try {
    return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch (_) {
    return 'es';
  }
}

export function getStoredLanguage() {
  try {
    const raw = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (raw === null || raw === undefined || raw === '') return null;
    const normalized = normalizeLanguage(raw);
    return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : null;
  } catch (_) {
    return null;
  }
}

function lookup(dict, key) {
  if (!dict || !key) return undefined;
  return String(key).split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), dict);
}

function interpolate(value, params = {}) {
  if (typeof value !== 'string') return value;
  return value.replace(/\{(\w+)\}/g, (_, name) => {
    if (params[name] === undefined || params[name] === null) return '';
    return String(params[name]);
  });
}

export function translate(key, params = {}, lang = getCurrentLanguage()) {
  const normalized = normalizeLanguage(lang);
  const value = lookup(dictionaries[normalized], key);
  const fallback = lookup(dictionaries.es, key);
  const resolved = value !== undefined ? value : fallback;
  if (resolved === undefined) return String(key || '');
  if (Array.isArray(resolved)) return resolved;
  return interpolate(resolved, params);
}

const I18nContext = createContext({
  language: 'es',
  setLanguage: () => {},
  syncLanguage: () => {},
  t: translate
});

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getCurrentLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguageState(normalized);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    } catch (_) {}
  }, []);

  const syncLanguage = useCallback((nextLanguage) => {
    if (!nextLanguage) return;
    setLanguage(nextLanguage);
  }, [setLanguage]);

  useEffect(() => {
    try {
      document.documentElement.lang = language;
      document.title = language === 'en' ? 'Qproject' : 'Qproject';
    } catch (_) {}
  }, [language]);

  const t = useCallback((key, params = {}) => translate(key, params, language), [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    syncLanguage,
    t
  }), [language, setLanguage, syncLanguage, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useTranslation() {
  return useI18n();
}

export default I18nContext;
